import { posthog, isPostHogEnabled } from './posthog';
import { APP_NAME } from './env';
import { IGNORED_ERROR_CODES } from './constants';
import { wasCaptured } from './error-utils';

type ErrorSource =
  | 'checkout'
  | 'api'
  | 'ui_crash'
  | 'global_crash'
  | 'illustration'
  | 'app_icon'
  | 'unhandled';

type ErrorSeverity = 'fatal' | 'error' | 'warning';

interface CaptureErrorOptions {
  error: unknown;
  source: ErrorSource;
  severity?: ErrorSeverity;
  /** Extra properties — e.g. checkout_type, endpoint, cart_item_count */
  context?: Record<string, unknown>;
}

/**
 * Captures an error via posthog.captureException() — the SDK's official API.
 *
 * posthog.capture('$exception') is unreliable: it skips required metadata
 * ($exception_list, $exception_level, _originatedFromCaptureException flag)
 * so events land in Activity only and never create Error Tracking issues
 * or trigger Slack alerts.
 *
 * captureException() lets the SDK build $exception_list from the Error,
 * sends through sendExceptionEvent with _noTruncate, and properly enters
 * the Error Tracking pipeline.
 */
export function captureError({
  error,
  source,
  severity = 'error',
  context = {},
}: CaptureErrorOptions): void {
  try {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    // The adapter already reported this one. A caller adding its own context
    // still calls in, so the check belongs here rather than at each call site.
    if (wasCaptured(error)) {
      return;
    }

    if (IGNORED_ERROR_CODES.includes(errorObj.name)) {
      return;
    }

    // ChunkLoadError is a stale-deployment artifact (user had old tab open).
    // Not a real app error — skip PostHog.
    if (errorObj.name === 'ChunkLoadError') {
      return;
    }

    const properties = {
      app_name: APP_NAME,
      error_source: source,
      error_severity: severity,
      exception_timestamp: new Date().toISOString(),
      ...context,
    };

    // posthog-js only init()s in the browser, and captureException on an
    // uninitialised instance neither throws nor warns — it discards the event.
    // Server renders would therefore report nothing at all, so they go to the
    // process log, which is the sink that actually collects them.
    if (typeof window === 'undefined') {
      console.error('[error]', errorObj.message, properties, errorObj.stack);
      return;
    }

    if (!isPostHogEnabled()) return;

    posthog.captureException(errorObj, {
      ...properties,
      current_route: window.location.pathname,
      current_page: window.location.href,
    });
  } catch (captureErr) {
    console.error('[PostHog] Failed to capture error:', captureErr);
  }
}
